'use client';
import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useEvent } from '@/lib/eventContext';
import EventSwitchModal from './EventSwitchModal';
import Taskbar from './Taskbar';
import LockScreen from './LockScreen';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { useTheme } from '@/contexts/ThemeContext';
import { AlertTriangle, Radio, Users, Calendar, FolderOpen, BarChart2, Building2, Bell, ChevronDown, User, LogOut, Shield, UserPlus, Sparkles, PhoneCall, X, Sun, Moon, Archive, FileText, Inbox, Smartphone, Monitor, LayoutGrid, Car, Truck, Zap } from 'lucide-react';

const ALL_DESKTOP_APPS = [
  { id: 'dispatch', label: 'Dispatch', sublabel: 'Meldkamer', icon: AlertTriangle, route: '/dispatch-meldkamer', color: '#ef4444', roles: null },
  { id: 'dispatch-wachtrij', label: 'Wachtrij', sublabel: 'Dispatch Queue', icon: Inbox, route: '/dispatch-wachtrij', color: '#22c55e', roles: null },
  { id: 'uitvraag-centralist', label: 'Uitvraag', sublabel: 'Centralist', icon: PhoneCall, route: '/uitvraag-centralist', color: '#00ccff', roles: null },
  { id: 'communicatie', label: 'Communicatie', sublabel: 'Chat & Broadcast', icon: Radio, route: '/communicatie', color: '#0ea5e9', roles: null },
  { id: 'post-overzicht', label: 'Post Overzicht', sublabel: 'Wie doet wat', icon: LayoutGrid, route: '/post-overzicht', color: '#f59e0b', roles: null },
  { id: 'hr', label: 'HR', sublabel: 'Personeel', icon: Users, route: '/hr', color: '#8b5cf6', roles: ['Admin', 'ADMIN', 'HR / Planner', 'Administratie'] },
  { id: 'planning', label: 'Planning', sublabel: 'Shifts & Roosters', icon: Calendar, route: '/planning', color: '#10b981', roles: null },
  { id: 'documenten', label: 'Documenten', sublabel: 'Bestanden', icon: FolderOpen, route: '#', color: '#f59e0b', roles: null },
  { id: 'overview', label: 'Overview', sublabel: 'Event Dashboard', icon: BarChart2, route: '/overview-event-dashboard', color: '#0ea5e9', roles: null },
  { id: 'evenementen', label: 'Evenementen', sublabel: 'Afdeling', icon: Building2, route: '/evenementen-afdeling', color: '#ec4899', roles: null },
  { id: 'archief', label: 'Archief', sublabel: 'Historisch Overzicht', icon: Archive, route: '/archief', color: '#f59e0b', roles: null },
  { id: 'incident-rapportage', label: 'Rapportage', sublabel: 'Incident Rapport', icon: FileText, route: '/incident-rapportage', color: '#ef4444', roles: null },
  { id: 'account-aanmaken', label: 'Account', sublabel: 'Aanmaken', icon: UserPlus, route: '/account-aanmaken', color: '#a78bfa', roles: ['Admin', 'ADMIN', 'HR / Planner'] },
  { id: 'schoonmaak', label: 'Schoonmaak', sublabel: 'Taken & Protocollen', icon: Sparkles, route: '/schoonmaak', color: '#10b981', roles: null },
  { id: 'wagenpark', label: 'Wagenpark', sublabel: 'Voertuigen', icon: Truck, route: '/wagenpark', color: '#0ea5e9', roles: null },
  { id: 'in-de-wagen', label: 'In de Wagen', sublabel: 'Status & Checklist', icon: Car, route: '/in-de-wagen', color: '#f59e0b', roles: null },
  { id: 'nieuw-evenement', label: 'Nieuw Evenement', sublabel: 'Aanmaken', icon: Calendar, route: '/nieuw-evenement', color: '#ec4899', roles: null },
  { id: 'actieve-incidenten', label: 'Actieve Incidenten', sublabel: 'Dispatch Beheer', icon: Zap, route: '/actieve-incidenten', color: '#ec4899', roles: null },
];

// Apps shown in the mobile bottom nav (most used)
const MOBILE_NAV_APPS = ['dispatch', 'dispatch-wachtrij', 'uitvraag-centralist', 'communicatie', 'planning'];

interface NotificationItem {
  id: string;
  text: string;
  time: string;
  type: 'alert' | 'info' | 'warning';
}

const MOCK_NOTIFICATIONS: NotificationItem[] = [
  { id: 'n-001', text: 'INC-2026-0045: Drugsoverdosis camping — geen eenheid toegewezen', time: '07:01', type: 'alert' },
  { id: 'n-002', text: 'INC-2026-0043: Verloren kind — wacht op toewijzing', time: '06:45', type: 'warning' },
  { id: 'n-003', text: 'EHBO Bravo is beschikbaar', time: '06:30', type: 'info' },
];

export default function Desktop({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { activeEvent, setActiveEvent, events, pendingEvent } = useEvent();
  const { user, loading, getUserProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [userProfile, setUserProfile] = useState<{ full_name: string; role: string } | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>(MOCK_NOTIFICATIONS);
  const [mounted, setMounted] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [mobileNavPage, setMobileNavPage] = useState(0);

  const currentRole: string = userProfile?.role ?? user?.user_metadata?.role ?? user?.app_metadata?.role ?? 'Admin';
  const displayName: string = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Gebruiker';

  const DESKTOP_APPS = ALL_DESKTOP_APPS.filter(app =>
    app.roles === null || app.roles.includes(currentRole)
  );

  // Mobile bottom nav: first 4 visible apps + "Meer" slot
  const MOBILE_PRIMARY_APPS = DESKTOP_APPS.filter(a => MOBILE_NAV_APPS.includes(a.id)).slice(0, 4);
  const MOBILE_MORE_APPS = DESKTOP_APPS.filter(a => !MOBILE_NAV_APPS.includes(a.id) || DESKTOP_APPS.indexOf(a) >= 4);

  // Auth guard — redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      router.replace('/login');
    }
  }, [loading, user, router]);

  // Fetch user profile from database
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return;
    }
    setUserProfile(null);
    getUserProfile()
      .then((profile: any) => {
        if (profile) setUserProfile({ full_name: profile.full_name, role: profile.role });
      })
      .catch(() => {});
  }, [user, getUserProfile]);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    };
    setMounted(true);
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleLock = () => setIsLocked(true);
    window.addEventListener('watchtower:lock', handleLock);
    return () => window.removeEventListener('watchtower:lock', handleLock);
  }, []);

  const handleLogout = async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const accessToken = currentSession?.access_token;
      Object.keys(localStorage)
        .filter(k => k.startsWith('sb-') || k.includes('supabase'))
        .forEach(k => localStorage.removeItem(k));
      await fetch('/api/logout', {
        method: 'POST',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
    } catch {
      // Ignore errors — always redirect
    }
    window.location.href = '/login';
  };

  const handleAppClick = (route: string) => {
    if (route !== '#') router.push(route);
  };

  const activeAppId = DESKTOP_APPS.find(a => a.route === pathname)?.id;
  const activeApp = DESKTOP_APPS.find(a => a.route === pathname);

  // ─── MOBILE LAYOUT ───────────────────────────────────────────────────────────
  if (isMobileLayout) {
    return (
      <div suppressHydrationWarning className="flex flex-col h-screen w-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, #070c14 0%, #0a1628 50%, #070c14 100%)' }}>
        {isLocked && (
          <LockScreen
            onUnlock={() => setIsLocked(false)}
            userName={displayName}
            userRole={currentRole}
            userEmail={user?.email ?? ''}
          />
        )}

        {/* Mobile Header */}
        <header className="flex items-center justify-between px-3 h-11 shrink-0 border-b border-wt-border/50 z-[300]" style={{ background: 'rgba(10,15,26,0.97)', backdropFilter: 'blur(12px)' }}>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => router.push('/')}>
              <AppLogo size={22} />
              <span className="font-mono text-xs font-700 tracking-widest text-wt-primary uppercase">WATCHTOWER</span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Active page label */}
            {activeApp && (
              <span className="text-xs text-wt-muted font-medium px-2 py-0.5 rounded" style={{ background: `${activeApp.color}18`, color: activeApp.color }}>
                {activeApp.label}
              </span>
            )}

            {/* Clock */}
            <span className="font-mono text-xs text-wt-primary tabular-nums px-1">{mounted ? currentTime : '00:00:00'}</span>

            {/* Notifications */}
            <button
              onClick={() => { setNotifOpen(p => !p); setUserMenuOpen(false); }}
              className="relative p-1.5 rounded hover:bg-wt-elevated transition-colors"
            >
              <Bell size={15} className="text-wt-text-dim" />
              {notifications.length > 0 && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />}
            </button>

            {/* Switch to Desktop */}
            <button
              onClick={() => setIsMobileLayout(false)}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors hover:bg-wt-elevated"
              style={{ color: '#00ccff', border: '1px solid #00ccff40' }}
              title="Schakel naar bureaubladweergave"
            >
              <Monitor size={13} />
              <span className="hidden xs:inline">Desktop</span>
            </button>
          </div>
        </header>

        {/* Notification dropdown */}
        {notifOpen && (
          <div className="absolute top-11 right-0 left-0 mx-3 rounded-lg border border-wt-border z-[400]" style={{ background: '#0d1520', boxShadow: '0 20px 40px rgba(0,0,0,0.6)' }}>
            <div className="px-3 py-2 border-b border-wt-border flex items-center justify-between">
              <p className="text-xs font-medium text-wt-text">Meldingen</p>
              <button onClick={() => setNotifOpen(false)}><X size={13} className="text-wt-muted" /></button>
            </div>
            {notifications.length === 0 && (
              <div className="px-3 py-4 text-center"><p className="text-xs text-wt-muted">Geen meldingen</p></div>
            )}
            {notifications.map(n => (
              <div key={`mnotif-${n.id}`} className="px-3 py-2.5 border-b border-wt-border/50">
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.type === 'alert' ? 'bg-red-500' : n.type === 'warning' ? 'bg-yellow-500' : 'bg-wt-primary'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-wt-text leading-relaxed">{n.text}</p>
                    <p className="text-xs text-wt-muted mt-0.5">{n.time}</p>
                  </div>
                  <button
                    onClick={() => setNotifications(prev => prev.filter(x => x.id !== n.id))}
                    className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-white/10"
                  >
                    <X size={11} className="text-wt-muted" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className="shrink-0 border-t border-wt-border/50 z-[300]" style={{ background: 'rgba(7,12,20,0.97)', backdropFilter: 'blur(16px)' }}>
          {/* Primary nav row */}
          <div className="flex items-stretch h-16">
            {MOBILE_PRIMARY_APPS.map(app => {
              const isActive = app.id === activeAppId;
              return (
                <button
                  key={`mnav-${app.id}`}
                  onClick={() => handleAppClick(app.route)}
                  className="flex-1 flex flex-col items-center justify-center gap-1 transition-all duration-150 relative min-w-0 px-1"
                  style={{ borderTop: isActive ? `2px solid ${app.color}` : '2px solid transparent' }}
                >
                  <app.icon size={20} style={{ color: isActive ? app.color : '#7a9ab8' }} />
                  <span className="text-[9px] font-medium truncate w-full text-center" style={{ color: isActive ? app.color : '#7a9ab8' }}>{app.label}</span>
                </button>
              );
            })}

            {/* More apps button */}
            <button
              onClick={() => setMobileNavPage(p => p === 0 ? 1 : 0)}
              className="flex-1 flex flex-col items-center justify-center gap-1 transition-all duration-150 min-w-0 px-1"
              style={{ borderTop: mobileNavPage === 1 ? '2px solid #7a9ab8' : '2px solid transparent' }}
            >
              <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
                {[0,1,2,3].map(i => <div key={i} className="rounded-sm" style={{ background: '#7a9ab8', width: 6, height: 6 }} />)}
              </div>
              <span className="text-[10px] font-medium text-wt-muted">Meer</span>
            </button>
          </div>

          {/* Expanded "Meer" apps grid */}
          {mobileNavPage === 1 && (
            <div className="border-t border-wt-border/40 px-3 py-3 grid grid-cols-4 gap-2">
              {DESKTOP_APPS.map(app => {
                const isActive = app.id === activeAppId;
                return (
                  <button
                    key={`mmore-${app.id}`}
                    onClick={() => { handleAppClick(app.route); setMobileNavPage(0); }}
                    className="flex flex-col items-center gap-1 p-2 rounded-xl transition-all w-full"
                    style={{ background: isActive ? `${app.color}22` : 'rgba(255,255,255,0.04)', border: isActive ? `1px solid ${app.color}50` : '1px solid transparent' }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${app.color}18` }}>
                      <app.icon size={18} style={{ color: app.color }} />
                    </div>
                    <span className="text-[9px] font-medium text-center leading-tight w-full truncate" style={{ color: isActive ? app.color : '#a0b8cc' }}>{app.label}</span>
                    <span className="text-[8px] text-center leading-tight w-full truncate text-wt-muted">{app.sublabel}</span>
                  </button>
                );
              })}
            </div>
          )}
        </nav>

        {pendingEvent && <EventSwitchModal />}
        {(notifOpen || userMenuOpen) && (
          <div className="fixed inset-0 z-[350]" onClick={() => { setNotifOpen(false); setUserMenuOpen(false); }} />
        )}
      </div>
    );
  }

  // ─── DESKTOP LAYOUT (original) ───────────────────────────────────────────────
  return (
    <div suppressHydrationWarning className="wt-desktop-bg flex flex-col h-screen w-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, #070c14 0%, #0a1628 50%, #070c14 100%)' }}>
      {/* Lock Screen overlay */}
      {isLocked && (
        <LockScreen
          onUnlock={() => setIsLocked(false)}
          userName={displayName}
          userRole={currentRole}
          userEmail={user?.email ?? ''}
        />
      )}

      {/* Header / Topbar */}
      <header className="wt-header flex items-center justify-between px-4 h-12 shrink-0 border-b border-wt-border/50 relative z-[300]" style={{ background: 'rgba(10,15,26,0.95)', backdropFilter: 'blur(12px)' }}>
        {/* Left: Logo + Brand */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => router.push('/')}>
            <AppLogo size={28} />
            <span className="font-mono text-sm font-700 tracking-widest text-wt-primary uppercase">WATCHTOWER</span>
          </div>
          <div className="w-px h-5 bg-wt-border mx-2" />
        </div>

        {/* Right: Notifications + User */}
        <div className="flex items-center gap-2">
          {/* Mobile Layout Toggle */}
          <button
            onClick={() => setIsMobileLayout(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors hover:bg-wt-elevated"
            style={{ color: '#00ccff', border: '1px solid #00ccff40' }}
            title="Schakel naar mobiele weergave"
          >
            <Smartphone size={14} />
            <span className="hidden sm:inline">Mobiel</span>
          </button>

          {/* Day/Night Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded hover:bg-wt-elevated transition-colors"
            title={theme === 'dark' ? 'Schakel naar dagmodus' : 'Schakel naar nachtmodus'}
          >
            {theme === 'dark' ? (
              <Sun size={16} className="text-wt-text-dim hover:text-yellow-400 transition-colors" />
            ) : (
              <Moon size={16} className="text-wt-text-dim hover:text-wt-primary transition-colors" />
            )}
          </button>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => { setNotifOpen(p => !p); setEventDropdownOpen(false); setUserMenuOpen(false); }}
              className="relative p-2 rounded hover:bg-wt-elevated transition-colors"
            >
              <Bell size={16} className="text-wt-text-dim" />
              {notifications.length > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />}
            </button>
            {notifOpen && (
              <div className="absolute top-full mt-1 right-0 w-80 rounded-lg border border-wt-border z-[300] animate-slide-up" style={{ background: '#0d1520', boxShadow: '0 20px 40px rgba(0,0,0,0.6)' }}>
                <div className="px-3 py-2 border-b border-wt-border flex items-center justify-between">
                  <p className="text-xs font-medium text-wt-text">Meldingen</p>
                  <span className="text-xs text-wt-primary">{notifications.length} nieuw</span>
                </div>
                {notifications.length === 0 && (
                  <div className="px-3 py-4 text-center">
                    <p className="text-xs text-wt-muted">Geen meldingen</p>
                  </div>
                )}
                {notifications.map(n => (
                  <div key={`notif-${n.id}`} className="px-3 py-2.5 border-b border-wt-border/50 hover:bg-wt-elevated transition-colors">
                    <div className="flex items-start gap-2">
                      <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.type === 'alert' ? 'bg-red-500' : n.type === 'warning' ? 'bg-yellow-500' : 'bg-wt-primary'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-wt-text leading-relaxed">{n.text}</p>
                        <p className="text-xs text-wt-muted mt-0.5">{n.time}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setNotifications(prev => prev.filter(x => x.id !== n.id)); }}
                        className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-white/10 transition-colors ml-1"
                        title="Melding verwijderen"
                      >
                        <X size={11} className="text-wt-muted hover:text-wt-text" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => { setUserMenuOpen(p => !p); setNotifOpen(false); setEventDropdownOpen(false); }}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-wt-elevated transition-colors"
            >
              <div className="w-6 h-6 rounded-full bg-wt-primary/20 border border-wt-primary/40 flex items-center justify-center">
                <Shield size={12} className="text-wt-primary" />
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-xs font-medium text-wt-text leading-none">{displayName}</p>
                <p className="text-xs text-wt-muted leading-none mt-0.5">{currentRole}</p>
              </div>
              <ChevronDown size={12} className="text-wt-muted" />
            </button>
            {userMenuOpen && (
              <div className="absolute top-full mt-1 right-0 w-56 rounded-lg border border-wt-border z-[300] animate-slide-up overflow-hidden" style={{ background: '#0d1520', boxShadow: '0 20px 40px rgba(0,0,0,0.6)' }}>
                <div className="px-3 py-3 border-b border-wt-border/60 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-wt-primary/20 border border-wt-primary/40 flex items-center justify-center shrink-0">
                    <span className="text-sm font-semibold text-wt-primary uppercase">
                      {displayName.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-wt-text truncate">{displayName}</p>
                    <p className="text-xs text-wt-primary truncate">{currentRole}</p>
                    {user?.email && (
                      <p className="text-xs text-wt-muted truncate">{user.email}</p>
                    )}
                  </div>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setUserMenuOpen(false); router.push('/profiel'); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-wt-elevated transition-colors"
                  >
                    <User size={14} className="text-wt-muted" />
                    <span className="text-sm text-wt-text">Profiel</span>
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); setIsLocked(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-wt-elevated transition-colors"
                  >
                    <Shield size={14} className="text-wt-muted" />
                    <span className="text-sm text-wt-text">Vergrendelen</span>
                  </button>
                  <div className="my-1 border-t border-wt-border/40" />
                  <button
                    type="button"
                    onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg bg-red-600 hover:bg-red-700 active:bg-red-800 transition-colors text-white font-semibold cursor-pointer"
                  >
                    <LogOut size={14} className="text-white" />
                    <span className="text-sm text-white">Uitloggen</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Clock */}
          <div className="px-2 py-1 rounded border border-wt-border/50">
            <span className="font-mono text-xs text-wt-primary tabular-nums">{mounted ? currentTime : '00:00:00'}</span>
          </div>
        </div>
      </header>

      {/* Desktop Area */}
      <div className="flex-1 overflow-hidden relative">
        <div className="h-full overflow-auto">
          {children}
        </div>
      </div>

      {/* Taskbar */}
      <Taskbar apps={DESKTOP_APPS} activeAppId={activeAppId} onAppClick={handleAppClick} currentTime={currentTime} />

      {/* Event Switch Confirmation Modal */}
      {pendingEvent && <EventSwitchModal />}

      {/* Click-outside overlay */}
      {(eventDropdownOpen || notifOpen || userMenuOpen) && (
        <div className="fixed inset-0 z-[150]" onClick={() => { setEventDropdownOpen(false); setNotifOpen(false); setUserMenuOpen(false); }} />
      )}
    </div>
  );
}